import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Avatar,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  IconButton,
  Pagination,
  Button,
  Tooltip,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Modal,
  Grid,
  Chip,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SaveIcon from '@mui/icons-material/Save';
import FolderIcon from '@mui/icons-material/Folder';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { alpha } from '@mui/material/styles';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import * as XLSX from 'xlsx';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import { hasActionPermission } from '../utils/permissions';
import UserActionMenu from '../components/UserActionMenu';
import AdminOTPDialog from '../components/AdminOTPDialog';

const drawerWidth = 240;
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";

const AllUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [balanceFilter, setBalanceFilter] = useState('');
  const [minBalance, setMinBalance] = useState('');
  const [maxBalance, setMaxBalance] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showBalances, setShowBalances] = useState({});
  const [userPermissions, setUserPermissions] = useState([]);
  const [userType, setUserType] = useState('admin');
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [fundDialogOpen, setFundDialogOpen] = useState(false);
  const [fundAmount, setFundAmount] = useState('');
  const [fundType, setFundType] = useState('credit');
  const [fundingUser, setFundingUser] = useState(null);
  const [isFunding, setIsFunding] = useState(false);
  const [fundWalletOpen, setFundWalletOpen] = useState(false);
  const [amount, setAmount] = useState('');


  // OTP Dialog State
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [otpOperationType, setOtpOperationType] = useState('fund_user');
  const [pendingTransaction, setPendingTransaction] = useState(null);

  // Bulk fund wallet functionality
  const [bulkFundOpen, setBulkFundOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userAmounts, setUserAmounts] = useState({});
  const [bulkNarration, setBulkNarration] = useState('');
  const [bulkPurpose, setBulkPurpose] = useState('');
  const [bulkFundType, setBulkFundType] = useState('credit');
  const [isBulkFunding, setIsBulkFunding] = useState(false);
  const [bulkSuccessMessage, setBulkSuccessMessage] = useState('');
  const [bulkUserFilter, setBulkUserFilter] = useState('');
  const [bulkUserPage, setBulkUserPage] = useState(1);

  // Beneficiary groups functionality
  const [beneficiaryGroups, setBeneficiaryGroups] = useState([]);
  const [saveGroupOpen, setSaveGroupOpen] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [isSavingGroup, setIsSavingGroup] = useState(false);
  const [loadGroupsOpen, setLoadGroupsOpen] = useState(false);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [useGroupOpen, setUseGroupOpen] = useState(false);
  const [isUsingGroup, setIsUsingGroup] = useState(false);
  const [groupFundType, setGroupFundType] = useState('credit');
  const [groupNarration, setGroupNarration] = useState('');
  const [groupBeneficiaryAmounts, setGroupBeneficiaryAmounts] = useState({});

  // Debounced search
  const searchTimeoutRef = useRef(null);
  const [debouncedBulkUserFilter, setDebouncedBulkUserFilter] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setUserPermissions(parsedUser.permissions || []);
        setUserType(parsedUser.type || 'admin');
      }
    } catch (error) {
      console.error('Error parsing user data for permissions:', error);
    }
  }, []);

  const canViewUserBalances = useMemo(
    () => hasActionPermission('view_balance', userPermissions, userType),
    [userPermissions, userType]
  );


  const [excelFile, setExcelFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importedUsers, setImportedUsers] = useState([]);
  const [showImportPreview, setShowImportPreview] = useState(false);

  const handleBulkUserPageChange = useCallback((event, value) => {
    setBulkUserPage(value);
  }, []);

  const fetchUsers = async () => {
    try {
      const data = await api.dashboard.getAllUsers();
      // Sort users by creation date (most recent first)
      const sortedUsers = data.sort((a, b) =>
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      setUsers(sortedUsers);
    } catch (err) {
      setError(err.message || 'Error fetching users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchBeneficiaryGroups();
  }, []);

  // Debounced search effect
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedBulkUserFilter(bulkUserFilter);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [bulkUserFilter]);

  // Memoized filtered users with debounced search
  const bulkFilteredUsers = useMemo(() => {
    return users.filter(
      (user) =>
        (user.name || user.fullname || '').toLowerCase().includes(debouncedBulkUserFilter.toLowerCase()) ||
        (user.email || '').toLowerCase().includes(debouncedBulkUserFilter.toLowerCase()) ||
        (user.username || '').toLowerCase().includes(debouncedBulkUserFilter.toLowerCase()) ||
        (user.phone || '').toLowerCase().includes(debouncedBulkUserFilter.toLowerCase()) ||
        (user.virtuaAccountNo || '').toLowerCase().includes(debouncedBulkUserFilter.toLowerCase())
    );
  }, [users, debouncedBulkUserFilter]);

  // Pagination for bulk fund users
  const bulkUsersPerPage = 20;
  const bulkUserTotalPages = Math.ceil(bulkFilteredUsers.length / bulkUsersPerPage);
  const paginatedBulkUsers = useMemo(() => {
    const startIndex = (bulkUserPage - 1) * bulkUsersPerPage;
    return bulkFilteredUsers.slice(startIndex, startIndex + bulkUsersPerPage);
  }, [bulkFilteredUsers, bulkUserPage]);

  const filteredUsers = users.filter((user) => {
    // Text filter
    const matchesText =
      (user.name || user.fullname || '').toLowerCase().includes(filter.toLowerCase()) ||
      (user.email || '').toLowerCase().includes(filter.toLowerCase()) ||
      (user.username || '').toLowerCase().includes(filter.toLowerCase()) ||
      (user.phone || '').toLowerCase().includes(filter.toLowerCase()) ||
      (user.virtuaAccountNo || '').toLowerCase().includes(filter.toLowerCase());

    // Balance filter
    const userBalance = Number(user.balance || 0);
    let matchesBalance = true;

    if (canViewUserBalances && balanceFilter) {
      switch (balanceFilter) {
        case 'high':
          matchesBalance = userBalance > 10000;
          break;
        case 'medium':
          matchesBalance = userBalance >= 1000 && userBalance <= 10000;
          break;
        case 'low':
          matchesBalance = userBalance < 1000;
          break;
        case 'zero':
          matchesBalance = userBalance === 0;
          break;
        case 'custom':
          const min = Number(minBalance) || 0;
          const max = Number(maxBalance) || Infinity;
          matchesBalance = userBalance >= min && userBalance <= max;
          break;
        default:
          matchesBalance = true;
      }
    }

    return matchesText && matchesBalance;
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = filteredUsers.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleAvatarClick = (user) => {
    setSelectedUser(user);
    setDialogOpen(true);
    // Log admin activity for viewing user details
    api.dashboard.logAdminActivity({
      action: 'VIEW_USER_DETAILS',
      details: `Viewed details for user: ${user.name || user.fullname}`,
      timestamp: new Date().toISOString(),
      adminId: localStorage.getItem('adminId'),
      adminName: localStorage.getItem('adminName')
    });
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedUser(null);
  };

  const maskBvn = (bvn) => {
    if (!bvn || typeof bvn !== 'string' || bvn.length < 6) return bvn || '-';
    return bvn.slice(0, 2) + '*****' + bvn.slice(-4);
  };

  const getStatusColor = (status) => '#22c55e';

  const getBalanceColor = (balance) => {
    if (typeof balance !== 'number') return '#111';
    if (balance > 10000) return '#22c55e'; // green
    if (balance >= 1000) return '#facc15'; // yellow
    return '#ef4444'; // red
  };

  const handleToggleBalance = (userId) => {
    setShowBalances((prev) => ({ ...prev, [userId]: !prev[userId] }));
  };

  const handleStatusChange = async (userId, newStatus) => {
    try {
      setUpdatingStatus(prev => ({ ...prev, [userId]: true }));
      await api.dashboard.updateUserStatus(userId, newStatus);

      // Update local state
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user._id === userId || user.id === userId
            ? { ...user, status: newStatus }
            : user
        )
      );

      // Log admin activity
      await api.dashboard.logAdminActivity({
        action: newStatus === 'active' ? 'ACTIVATE_USER' : 'SUSPEND_USER',
        details: `User ${newStatus === 'active' ? 'activated' : 'suspended'}`,
        timestamp: new Date().toISOString(),
        adminId: localStorage.getItem('adminId'),
        adminName: localStorage.getItem('adminName')
      });

      // Show success toast
      toast.success(`User ${newStatus === 'active' ? 'activated' : 'suspended'} successfully`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      console.error('Error updating user status:', error);
      // Show error toast
      toast.error(error.message || 'Failed to update user status', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleFundWallet = (user) => {
    setSelectedUser(user);
    setFundWalletOpen(true);
  };

  const handleFundDialogClose = () => {
    setFundDialogOpen(false);
    setFundingUser(null);
    setFundAmount('');
    setFundType('credit');
  };

  const handleFundSubmit = async () => {
    if (!fundAmount || isNaN(fundAmount) || Number(fundAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      setIsFunding(true);
      await api.dashboard.updateUserWallet(fundingUser._id || fundingUser.id, {
        amount: Number(fundAmount),
        type: fundType
      });

      // Update local state
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user._id === fundingUser._id || user.id === fundingUser.id
            ? {
              ...user,
              balance: fundType === 'credit'
                ? Number(user.balance) + Number(fundAmount)
                : Number(user.balance) - Number(fundAmount)
            }
            : user
        )
      );

      toast.success(`Wallet ${fundType === 'credit' ? 'credited' : 'debited'} successfully`);
      handleFundDialogClose();
    } catch (error) {
      console.error('Error funding wallet:', error);
      toast.error(error.message || 'Failed to update wallet');
    } finally {
      setIsFunding(false);
    }
  };

  const handleFundWalletClick = () => {
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    // Prepare transaction data
    const transactionData = {
      userId: selectedUser._id || selectedUser.id,
      data: {
        amount: Number(amount),
        type: fundType
      }
    };

    setPendingTransaction(transactionData);
    setOtpOperationType('fund_user');
    setOtpDialogOpen(true);
  };

  const handleOTPVerify = async (otp) => {
    try {
      setLoading(true);

      if (otpOperationType === 'fund_user') {
        const { userId, data } = pendingTransaction;
        // Include OTP in the data
        await api.dashboard.updateUserWallet(userId, { ...data, otp });

        // Update local state
        setUsers(prevUsers =>
          prevUsers.map(user =>
            user._id === userId || user.id === userId
              ? {
                ...user,
                balance: data.type === 'credit'
                  ? Number(user.balance) + data.amount
                  : Number(user.balance) - data.amount
              }
              : user
          )
        );

        toast.success(`Wallet ${data.type === 'credit' ? 'credited' : 'debited'} successfully`);
        setFundWalletOpen(false);
        setAmount('');
      } else if (otpOperationType === 'bulk_operation') {
        const { bulkFundData } = pendingTransaction;
        await api.dashboard.bulkFundWallets(bulkFundData, otp);

        // Update local state
        setUsers(prevUsers =>
          prevUsers.map(user => {
            const fundData = bulkFundData.find(fd => fd.userId === user._id);
            if (fundData) {
              const newBalance = fundData.type === 'credit'
                ? (user.balance || 0) + fundData.amount
                : Math.max(0, (user.balance || 0) - fundData.amount);
              return { ...user, balance: newBalance };
            }
            return user;
          })
        );

        // Show success message with levy information
        const levyCount = bulkFundData.filter(item =>
          item.type === 'credit' && Number(item.amount || 0) > 10000
        ).length;
        const levyMsg = levyCount > 0 ? ` (₦50 levy deducted from ${levyCount} amount${levyCount > 1 ? 's' : ''} over ₦10,000)` : '';
        const successMsg = `Successfully ${bulkFundData[0].type === 'credit' ? 'credited' : 'debited'} ${bulkFundData.length} user${bulkFundData.length > 1 ? 's' : ''}${levyMsg}`;
        toast.success(successMsg);
        setBulkSuccessMessage(successMsg);

        // Clear success message after 5 seconds
        setTimeout(() => setBulkSuccessMessage(''), 5000);

        handleBulkFundClose();
        setIsBulkFunding(false);
      }

      setOtpDialogOpen(false);
      setPendingTransaction(null);

    } catch (error) {
      console.error('Transaction error:', error);
      toast.error(error.message || 'Transaction failed');
    } finally {
      setLoading(false);
    }
  };

  // Bulk fund wallet handlers
  const handleBulkFundOpen = () => {
    setBulkFundOpen(true);
    setSelectedUsers([]);
    setUserAmounts({});
    setBulkNarration('');
    setBulkFundType('credit');
    setBulkUserFilter('');
  };

  const handleBulkFundClose = () => {
    setBulkFundOpen(false);
    setSelectedUsers([]);
    setUserAmounts({});
    setBulkNarration('');
    setBulkFundType('credit');
    setBulkSuccessMessage('');
    setBulkUserFilter('');
  };

  // Debounced search effect
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedBulkUserFilter(bulkUserFilter);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [bulkUserFilter]);

  // Optimized search handler
  const handleBulkUserFilterChange = useCallback((e) => {
    setBulkUserFilter(e.target.value);
  }, []);

  // Memoized user selection status
  const userSelectionStatus = useMemo(() => {
    const status = {};
    bulkFilteredUsers.forEach(user => {
      status[user._id] = selectedUsers.some(u => u._id === user._id);
    });
    return status;
  }, [bulkFilteredUsers, selectedUsers]);

  // Optimized handlers with useCallback
  const handleUserSelection = useCallback((user, isSelected) => {
    setSelectedUsers(prev => {
      if (isSelected) {
        return [...prev, user];
      } else {
        return prev.filter(u => u._id !== user._id);
      }
    });

    setUserAmounts(prev => {
      if (isSelected) {
        return { ...prev, [user._id]: '' };
      } else {
        const newAmounts = { ...prev };
        delete newAmounts[user._id];
        return newAmounts;
      }
    });
  }, []);

  // Amount input handler
  const handleAmountChange = useCallback((userId, amount) => {
    setUserAmounts(prev => ({ ...prev, [userId]: amount }));
  }, []);

  // Quick amount setters for common amounts
  const setAmountForSelected = useCallback((amount) => {
    setUserAmounts(prev => {
      const newAmounts = { ...prev };
      selectedUsers.forEach(user => {
        newAmounts[user._id] = amount.toString();
      });
      return newAmounts;
    });
  }, [selectedUsers]);

  const handleSelectAllBulk = useCallback((event) => {
    if (event.target.checked) {
      const allUsers = bulkFilteredUsers;
      setSelectedUsers(allUsers);
      const newAmounts = {};
      allUsers.forEach(user => {
        newAmounts[user._id] = '';
      });
      setUserAmounts(newAmounts);
    } else {
      setSelectedUsers([]);
      setUserAmounts({});
    }
  }, [bulkFilteredUsers]);

  const handleBulkFundSubmit = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }

    const usersWithAmounts = selectedUsers.filter(user =>
      userAmounts[user._id] && Number(userAmounts[user._id]) > 0
    );

    if (usersWithAmounts.length === 0) {
      toast.error('Please enter amounts for at least one user');
      return;
    }

    if (!bulkNarration.trim()) {
      toast.error('Please enter a narration for the transaction');
      return;
    }

    if (!bulkPurpose) {
      toast.error('Please select a purpose for the transaction');
      return;
    }

    setIsBulkFunding(true);
    try {
      const bulkFundData = usersWithAmounts.map(user => ({
        userId: user._id,
        amount: Number(userAmounts[user._id]),
        type: bulkFundType,
        narration: bulkNarration,
        purpose: bulkPurpose
      }));

      // Set pending transaction data and open OTP dialog
      setPendingTransaction({ bulkFundData });
      setOtpOperationType('bulk_operation');
      setOtpDialogOpen(true);

      // Note: Actual API call moved to handleOTPVerify

    } catch (error) {
      console.error('Error preparing bulk fund:', error);
      toast.error(error.message || 'Failed to prepare transaction');
      setIsBulkFunding(false);
    }
  };

  // Beneficiary groups handlers
  const handleSaveGroupOpen = () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user to save as a group');
      return;
    }
    setSaveGroupOpen(true);
    setGroupName('');
    setGroupDescription('');
  };

  const handleSaveGroupClose = () => {
    setSaveGroupOpen(false);
    setGroupName('');
    setGroupDescription('');
  };

  const handleSaveGroup = async () => {
    if (!groupName.trim()) {
      toast.error('Please enter a group name');
      return;
    }

    const usersWithAmounts = selectedUsers.filter(user =>
      userAmounts[user._id] && Number(userAmounts[user._id]) > 0
    );

    if (usersWithAmounts.length === 0) {
      toast.error('Please enter amounts for at least one user');
      return;
    }

    setIsSavingGroup(true);
    try {
      const beneficiaries = usersWithAmounts.map(user => ({
        userId: user._id,
        userName: user.name || user.fullname,
        userEmail: user.email,
        amount: Number(userAmounts[user._id]),
        narration: bulkNarration || '',
        purpose: bulkPurpose || ''
      }));

      // Calculate total amount
      const totalAmount = beneficiaries.reduce((sum, beneficiary) => sum + beneficiary.amount, 0);

      await api.dashboard.createBeneficiaryGroup({
        name: groupName,
        description: groupDescription,
        beneficiaries,
        totalAmount
      });

      toast.success(`Group "${groupName}" saved successfully`);
      handleSaveGroupClose();

      // Refresh groups list
      fetchBeneficiaryGroups();
    } catch (error) {
      console.error('Error saving group:', error);
      toast.error(error.message || 'Failed to save group');
    } finally {
      setIsSavingGroup(false);
    }
  };

  const fetchBeneficiaryGroups = async () => {
    try {
      const response = await api.dashboard.getBeneficiaryGroups();
      setBeneficiaryGroups(response.data || []);
    } catch (error) {
      console.error('Error fetching beneficiary groups:', error);
      // Don't show error toast as this might be called on component mount
    }
  };

  const handleLoadGroupsOpen = () => {
    setLoadGroupsOpen(true);
    fetchBeneficiaryGroups();
  };

  const handleLoadGroupsClose = () => {
    setLoadGroupsOpen(false);
    setSelectedGroup(null);
  };

  const handleLoadGroup = (group) => {
    setSelectedGroup(group);
    setSelectedUsers(group.beneficiaries.map(b => ({ _id: b.userId, name: b.userName, fullname: b.userName, email: b.userEmail })));
    const newAmounts = {};
    group.beneficiaries.forEach(b => {
      newAmounts[b.userId] = b.amount.toString();
    });
    setUserAmounts(newAmounts);
    setBulkNarration(group.description || '');
    setBulkPurpose(group.beneficiaries[0]?.purpose || '');

    // Reorder users to show selected group beneficiaries at the top
    const selectedUserIds = group.beneficiaries.map(b => b.userId);
    const reorderedUsers = [
      ...users.filter(user => selectedUserIds.includes(user._id)),
      ...users.filter(user => !selectedUserIds.includes(user._id))
    ];
    setUsers(reorderedUsers);

    handleLoadGroupsClose();
    toast.success(`Loaded group "${group.name}" with ${group.beneficiaries.length} beneficiaries`);
  };

  const handleDeleteGroup = async (groupId, groupName) => {
    if (!window.confirm(`Are you sure you want to delete the group "${groupName}"?`)) {
      return;
    }

    try {
      await api.dashboard.deleteBeneficiaryGroup(groupId);
      toast.success(`Group "${groupName}" deleted successfully`);
      fetchBeneficiaryGroups();
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error(error.message || 'Failed to delete group');
    }
  };

  const handleUseGroupOpen = (group) => {
    setSelectedGroup(group);
    setUseGroupOpen(true);
    setGroupFundType('credit');
    setGroupNarration(group.description || '');
    // Initialize amounts with the group's original amounts
    const initialAmounts = {};
    group.beneficiaries.forEach(beneficiary => {
      initialAmounts[beneficiary.userId] = beneficiary.amount.toString();
    });
    setGroupBeneficiaryAmounts(initialAmounts);
  };

  const handleUseGroupClose = () => {
    setUseGroupOpen(false);
    setSelectedGroup(null);
    setGroupFundType('credit');
    setGroupNarration('');
    setGroupBeneficiaryAmounts({});
  };

  const handleUseGroup = async () => {
    if (!selectedGroup) return;

    setIsUsingGroup(true);
    try {
      await api.dashboard.useBeneficiaryGroup(selectedGroup._id, {
        groupId: selectedGroup._id,
        fundType: groupFundType,
        narration: groupNarration,
        purpose: groupNarration || selectedGroup.beneficiaries[0]?.purpose || 'Bulk Admin Wallet Update'
      });

      toast.success(`Successfully processed ${selectedGroup.beneficiaries.length} beneficiaries from group "${selectedGroup.name}"`);
      handleUseGroupClose();

      // Refresh user list to show updated balances
      fetchUsers();
    } catch (error) {
      console.error('Error using group:', error);
      toast.error(error.message || 'Failed to use group');
    } finally {
      setIsUsingGroup(false);
    }
  };

  const handleGroupBeneficiaryAmountChange = (userId, amount) => {
    setGroupBeneficiaryAmounts(prev => ({ ...prev, [userId]: amount }));
  };

  // Excel import/export handlers
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setExcelFile(file);
      handleExcelImport(file);
    }
  };

  const handleExcelImport = async (file) => {
    try {
      setIsImporting(true);
      setExcelFile(file);

      const data = await readExcelFile(file);
      const parsedData = parseExcelData(data);

      // Fetch user details for each virtual account number
      const usersWithDetails = [];
      const errors = [];

      for (const item of parsedData) {
        try {
          // Fetch user details by virtual account number or phone using the API service
          const response = await fetch(`${API_BASE_URL}/by-virtual-account/${item.identifier}`, {
            method: 'GET',
            headers: api.getAuthHeaders(),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'User not found');
          }

          const user = await response.json();

          usersWithDetails.push({
            userId: user._id,
            username: user.username,
            fullname: user.fullname,
            email: user.email,
            identifier: item.identifier,
            amount: item.amount,
            narration: item.narration,
            purpose: item.purpose
          });
        } catch (error) {
          errors.push(`Identifier ${item.identifier}: ${error.message || 'User not found'}`);
        }
      }

      if (errors.length > 0) {
        toast.error(`Some users could not be found:\n${errors.join('\n')}`);
      }

      if (usersWithDetails.length === 0) {
        toast.error('No valid users found in the Excel file');
        return;
      }

      setImportedUsers(usersWithDetails);
      setShowImportPreview(true);
      toast.success(`Successfully imported ${usersWithDetails.length} users`);

    } catch (error) {
      console.error('Excel import error:', error);
      toast.error(error.message || 'Failed to import Excel file');
    } finally {
      setIsImporting(false);
    }
  };

  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const parseExcelData = (data) => {
    if (!data || data.length === 0) {
      throw new Error('No data found in Excel file');
    }

    console.log('Excel data columns found:', Object.keys(data[0] || {}));
    console.log('First row sample:', data[0]);

    // Expect columns: Identifier (VA or Phone), Amount, Narration (optional)
    return data.map((row, index) => {
      // More flexible column name matching
      const identifier =
        row['Phone Number'] ||
        row['phone_number'] ||
        row['Phone'] ||
        row['phone'] ||
        row['Mobile Number'] ||
        row['mobile_number'] ||
        row['Mobile'] ||
        row['mobile'] ||
        row['Virtual Account Number'] ||
        row['virtual_account_number'] ||
        row['VirtualAccountNumber'] ||
        row['Virtual Account'] ||
        row['virtual_account'] ||
        row['Account Number'] ||
        row['account_number'] ||
        row['Account'] ||
        row['account'] ||
        row['VA'] ||
        row['va'];

      const amount =
        row['Amount'] ||
        row['amount'] ||
        row['Sum'] ||
        row['sum'] ||
        row['Value'] ||
        row['value'];

      const narration =
        row['Narration'] ||
        row['narration'] ||
        row['Description'] ||
        row['description'] ||
        row['Note'] ||
        row['note'] ||
        row['Comment'] ||
        row['comment'] ||
        'Bulk fund via Excel import';

      const purpose =
        row['Purpose'] ||
        row['purpose'] ||
        row['Type'] ||
        row['type'] ||
        'Bulk Admin Wallet Update';

      if (!identifier) {
        throw new Error(`Row ${index + 1}: Phone Number or Virtual Account Number is required. Available columns: ${Object.keys(row).join(', ')}`);
      }
      if (!amount || isNaN(Number(amount))) {
        throw new Error(`Row ${index + 1}: Valid Amount is required. Found: ${amount}`);
      }

      return {
        identifier: identifier.toString().trim(),
        amount: Number(amount),
        narration: narration || 'Bulk fund via Excel import',
        purpose: purpose || 'Bulk Admin Wallet Update'
      };
    });
  };

  const downloadTemplate = () => {
    const templateData = [
      {
        'Phone Number': '08012345678',
        'Amount': 1000,
        'Narration': 'Sample transaction'
      },
      {
        'Virtual Account Number': '9784404538',
        'Amount': 2500,
        'Narration': 'Another transaction'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Bulk Fund Template');

    // Auto-size columns
    const colWidths = [
      { wch: 20 }, // Virtual Account Number
      { wch: 15 }, // Amount
      { wch: 20 }, // Purpose
      { wch: 30 }  // Narration
    ];
    ws['!cols'] = colWidths;

    XLSX.writeFile(wb, 'bulk_fund_template.xlsx');
    toast.success('Template downloaded successfully');
  };

  const handleImportConfirm = () => {
    // Replace selected users with imported users
    setSelectedUsers(importedUsers.map(user => ({
      _id: user.userId,
      username: user.username,
      fullname: user.fullname,
      amount: user.amount,
      narration: user.narration,
    })));
    setShowImportPreview(false);
    setImportedUsers([]);
    setExcelFile(null);
    toast.success('Imported users added to bulk fund selection');
  };

  const handleImportCancel = () => {
    setShowImportPreview(false);
    setImportedUsers([]);
    setExcelFile(null);
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} color="#A80A0A" gutterBottom>
        All Users
      </Typography>

      {/* Success Message */}
      {bulkSuccessMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setBulkSuccessMessage('')}
        >
          <Typography variant="body1" fontWeight={600}>
            {bulkSuccessMessage}
          </Typography>
        </Alert>
      )}

      {/* Bulk Fund Button */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AccountBalanceWalletIcon />}
          onClick={handleBulkFundOpen}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            py: 1,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            '&:hover': {
              boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
            },
          }}
        >
          Bulk Fund Wallets
        </Button>
        <Typography variant="body2" color="text.secondary">
          Send money to multiple users with different amounts
        </Typography>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Box sx={{ maxWidth: 400, minWidth: 300 }}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Filter by name, email, username or phone..."
            value={filter}
            onChange={e => setFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'background.paper',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                },
                '&.Mui-focused': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                },
              },
            }}
          />
        </Box>

        {/* Balance Filter */}
        {canViewUserBalances && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Balance Filter</InputLabel>
              <Select
                value={balanceFilter}
                label="Balance Filter"
                onChange={(e) => {
                  setBalanceFilter(e.target.value);
                  if (e.target.value !== 'custom') {
                    setMinBalance('');
                    setMaxBalance('');
                  }
                }}
                sx={{
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                }}
              >
                <MenuItem value="">All Balances</MenuItem>
                <MenuItem value="high">High (&gt;₦10,000)</MenuItem>
                <MenuItem value="medium">Medium (₦1,000-₦10,000)</MenuItem>
                <MenuItem value="low">Low (&lt;₦1,000)</MenuItem>
                <MenuItem value="zero">Zero Balance</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>

            {balanceFilter === 'custom' && (
              <>
                <TextField
                  size="small"
                  label="Min Balance"
                  type="number"
                  value={minBalance}
                  onChange={(e) => setMinBalance(e.target.value)}
                  inputProps={{ min: 0, step: 1 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                  }}
                  sx={{ width: 120 }}
                />
                <Typography variant="body2" color="text.secondary">
                  to
                </Typography>
                <TextField
                  size="small"
                  label="Max Balance"
                  type="number"
                  value={maxBalance}
                  onChange={(e) => setMaxBalance(e.target.value)}
                  inputProps={{ min: 0, step: 1 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                  }}
                  sx={{ width: 120 }}
                />
              </>
            )}
          </Box>
        )}
      </Box>
      {loading ? (
        <LoadingSpinner size="medium" />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          <Paper
            sx={{
              mt: 2,
              width: '100%',
              overflow: 'auto',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              '& .MuiTableHead-root': {
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
              },
              '& .MuiTableCell-head': {
                fontWeight: 700,
                color: theme.palette.text.primary,
                borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              },
              '& .MuiTableCell-body': {
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              },
              '& .MuiTableRow-root:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.02),
              },
            }}
          >
            <TableContainer sx={{ minWidth: isMobile ? 600 : 0 }}>
              <Table size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell>Profile</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    {canViewUserBalances && <TableCell>Balance</TableCell>}
                    <TableCell>KYC</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedUsers.map((user) => (
                    <TableRow
                      key={user._id || user.id}
                      sx={{
                        '&:last-child td': { borderBottom: 0 },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <TableCell>
                        <IconButton
                          onClick={() => handleAvatarClick(user)}
                          sx={{
                            '&:hover': {
                              transform: 'scale(1.1)',
                              transition: 'transform 0.2s ease',
                            },
                          }}
                        >
                          <Avatar
                            src={user.profilePic || ''}
                            alt={user.name || user.fullname || 'User'}
                            sx={{
                              width: 40,
                              height: 40,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            }}
                          >
                            {!(user.profilePic) && (user.name?.[0] || user.fullname?.[0] || '?')}
                          </Avatar>
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {user.name || user.fullname || '-'}
                          </Typography>
                          <UserActionMenu user={user} onStatusChange={(userId, newStatus) => handleStatusChange(userId, newStatus)} />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {user.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {user.phone || '-'}
                        </Typography>
                      </TableCell>
                      {canViewUserBalances && (
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <strong style={{
                              color: getBalanceColor(Number(user.balance)),
                              fontSize: '0.9rem',
                              fontWeight: 600,
                            }}>
                              {showBalances[user._id || user.id]
                                ? (user.balance !== undefined ? `₦${Number(user.balance).toLocaleString()}` : '-')
                                : (user.balance !== undefined ? '***' : '-')}
                            </strong>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleBalance(user._id || user.id)}
                              sx={{
                                ml: 1,
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                },
                              }}
                            >
                              {showBalances[user._id || user.id] ?
                                <VisibilityOffIcon fontSize="small" /> :
                                <VisibilityIcon fontSize="small" />
                              }
                            </IconButton>
                          </Box>
                        </TableCell>
                      )}
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Chip
                            label={`Tier ${user.kycTier || 0}`}
                            size="small"
                            color={user.kycTier >= 2 ? "success" : user.kycTier === 1 ? "primary" : "default"}
                            variant="outlined"
                          />
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {user.isKycninVerified && (
                              <Tooltip title={user.isNinNameMatched ? "NIN Match: OK" : `NIN Match: MISMATCH (Returned: ${user.ninResponseName || 'N/A'})`}>
                                <CheckCircleIcon sx={{ fontSize: 16, color: user.isNinNameMatched ? 'success.main' : 'warning.main' }} />
                              </Tooltip>
                            )}
                            {user.isKycbvnVerified && (
                              <Tooltip title={user.isBvnNameMatched ? "BVN Match: OK" : `BVN Match: MISMATCH (Returned: ${user.bvnResponseName || 'N/A'})`}>
                                <CheckCircleIcon sx={{ fontSize: 16, color: user.isBvnNameMatched ? 'success.main' : 'info.main' }} />
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {user.role || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          component="span"
                          sx={{
                            backgroundColor: getStatusColor(user.status),
                            color: '#fff',
                            px: 2,
                            py: 0.5,
                            borderRadius: 2,
                            fontWeight: 600,
                            fontSize: 12,
                            display: 'inline-block',
                            minWidth: 70,
                            textAlign: 'center',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          }}
                        >
                          {user.status || 'Active'}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          {user.status === 'active' ? (
                            <Tooltip title="Suspend User">
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<BlockIcon />}
                                onClick={() => handleStatusChange(user._id || user.id, 'suspended')}
                                disabled={updatingStatus[user._id || user.id]}
                                sx={{
                                  borderRadius: 2,
                                  textTransform: 'none',
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                                  },
                                }}
                              >
                                {updatingStatus[user._id || user.id] ? 'Updating...' : 'Suspend'}
                              </Button>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Activate User">
                              <Button
                                variant="outlined"
                                color="success"
                                size="small"
                                startIcon={<CheckCircleIcon />}
                                onClick={() => handleStatusChange(user._id || user.id, 'active')}
                                disabled={updatingStatus[user._id || user.id]}
                                sx={{
                                  borderRadius: 2,
                                  textTransform: 'none',
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                                  },
                                }}
                              >
                                {updatingStatus[user._id || user.id] ? 'Updating...' : 'Activate'}
                              </Button>
                            </Tooltip>
                          )}
                          <Tooltip title="Fund Wallet">
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              startIcon={<AccountBalanceWalletIcon />}
                              onClick={() => handleFundWallet(user)}
                              sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                },
                              }}
                            >
                              Fund
                            </Button>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          <Box display="flex" justifyContent="center" mt={3} mb={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: 1,
                  '&.Mui-selected': {
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  },
                },
              }}
            />
          </Box>
        </>
      )}

      {/* User Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 32px 64px rgba(0,0,0,0.4)'
              : '0 32px 64px rgba(0,0,0,0.15)',
            overflow: 'hidden',
            background: theme.palette.mode === 'dark'
              ? 'rgba(30,30,30,0.95)'
              : 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(20px)',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255,255,255,0.1)'
              : '1px solid rgba(255,255,255,0.2)',
            animation: 'slideIn 0.3s ease-out',
            '@keyframes slideIn': {
              '0%': { opacity: 0, transform: 'translateY(-20px) scale(0.95)' },
              '100%': { opacity: 1, transform: 'translateY(0) scale(1)' }
            }
          }
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary?.main || theme.palette.primary.dark})`,
            color: 'white',
            py: 3,
            px: 4,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 1,
              background: 'rgba(255, 255, 255, 0.1)',
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
              }}
            >
              <AccountBalanceWalletIcon sx={{ fontSize: 28, color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                User Profile
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Complete user information and details
              </Typography>
            </Box>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleDialogClose}
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{
          p: 0,
          bgcolor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.default, 0.5)
            : alpha(theme.palette.background.default, 0.3),
        }}>
          {selectedUser && (
            <>
              {/* Profile Header */}
              <Box
                sx={{
                  p: 5,
                  textAlign: 'center',
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
                    : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                  backdropFilter: 'blur(20px)',
                  borderBottom: theme.palette.mode === 'dark'
                    ? '1px solid rgba(255,255,255,0.1)'
                    : '1px solid rgba(255,255,255,0.3)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: theme.palette.mode === 'dark'
                      ? 'radial-gradient(circle at 30% 20%, rgba(120, 119, 198, 0.05) 0%, transparent 50%)'
                      : 'radial-gradient(circle at 30% 20%, rgba(120, 119, 198, 0.1) 0%, transparent 50%)',
                    pointerEvents: 'none'
                  }
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    display: 'inline-block',
                    mb: 3
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'inline-block',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: -8,
                        left: -8,
                        right: -8,
                        bottom: -8,
                        background: theme.palette.mode === 'dark'
                          ? 'linear-gradient(45deg, #667eea, #764ba2, #f093fb, #667eea)'
                          : 'linear-gradient(45deg, #667eea, #764ba2, #f093fb, #667eea)',
                        borderRadius: '50%',
                        backgroundSize: '400% 400%',
                        animation: 'gradientShift 3s ease infinite',
                        zIndex: -1
                      },
                      '@keyframes gradientShift': {
                        '0%, 100%': { backgroundPosition: '0% 50%' },
                        '50%': { backgroundPosition: '100% 50%' }
                      }
                    }}
                  >
                    <Avatar
                      src={selectedUser.profilePic || ''}
                      alt={selectedUser.name || selectedUser.fullname || 'User'}
                      sx={{
                        width: 120,
                        height: 120,
                        border: `6px solid ${theme.palette.background.paper}`,
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 16px 32px rgba(0,0,0,0.5)'
                          : '0 16px 32px rgba(0,0,0,0.2)',
                        fontSize: '3rem',
                        fontWeight: 700,
                        background: theme.palette.mode === 'dark'
                          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        position: 'relative',
                        zIndex: 1
                      }}
                    >
                      {!(selectedUser.profilePic) && (selectedUser.name?.[0] || selectedUser.fullname?.[0] || '?')}
                    </Avatar>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${getStatusColor(selectedUser.status)} 0%, ${alpha(getStatusColor(selectedUser.status), 0.8)} 100%)`,
                      border: `4px solid ${theme.palette.background.paper}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 12px rgba(0,0,0,0.5)'
                        : '0 4px 12px rgba(0,0,0,0.3)',
                      animation: 'pulse 2s infinite'
                    }}
                  >
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: 'white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}
                    />
                  </Box>
                </Box>
                <Typography variant="h3" fontWeight={800} sx={{
                  mb: 1,
                  color: theme.palette.text.primary,
                  textShadow: theme.palette.mode === 'dark' ? '0 2px 4px rgba(0,0,0,0.5)' : '0 2px 4px rgba(0,0,0,0.1)',
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  {selectedUser.name || selectedUser.fullname || '-'}
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 3, fontWeight: 500 }}>
                  {selectedUser.email}
                </Typography>
                <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap">
                  <Chip
                    label={selectedUser.role || 'User'}
                    color="primary"
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      fontWeight: 600,
                      borderWidth: 2,
                      px: 2,
                      py: 1,
                      fontSize: '0.9rem',
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.05)'
                        : 'rgba(255,255,255,0.8)',
                      backdropFilter: 'blur(10px)',
                      borderColor: theme.palette.primary.main
                    }}
                  />
                  <Chip
                    label={selectedUser.status || 'Active'}
                    sx={{
                      background: `linear-gradient(135deg, ${getStatusColor(selectedUser.status)} 0%, ${alpha(getStatusColor(selectedUser.status), 0.8)} 100%)`,
                      color: 'white',
                      borderRadius: 3,
                      fontWeight: 600,
                      px: 2,
                      py: 1,
                      fontSize: '0.9rem',
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 12px rgba(0,0,0,0.4)'
                        : '0 4px 12px rgba(0,0,0,0.2)',
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                    }}
                  />
                </Box>
              </Box>

              {/* User Details */}
              <Box sx={{ p: 4 }}>
                <Typography variant="h5" fontWeight={700} sx={{
                  mb: 4,
                  color: theme.palette.text.primary,
                  textAlign: 'center',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 60,
                    height: 3,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 2
                  }
                }}>
                  💼 Account Information
                </Typography>

                <Grid container spacing={3}>
                  {/* Balance Card */}
                  <Grid item xs={12}>
                    <Paper
                      sx={{
                        p: 4,
                        background: theme.palette.mode === 'dark'
                          ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.15)} 0%, ${alpha(theme.palette.success.light, 0.05)} 100%)`
                          : `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.light, 0.05)} 100%)`,
                        border: `2px solid ${alpha(theme.palette.success.main, 0.3)}`,
                        borderRadius: 4,
                        textAlign: 'center',
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 8px 32px rgba(0,0,0,0.3)'
                          : '0 8px 32px rgba(0,0,0,0.1)',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: 4,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        }
                      }}
                    >
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                        💰 Current Balance
                      </Typography>
                      <Typography
                        variant="h2"
                        fontWeight={800}
                        sx={{
                          color: getBalanceColor(Number(selectedUser.balance)),
                          textShadow: theme.palette.mode === 'dark' ? '0 4px 8px rgba(0,0,0,0.5)' : '0 4px 8px rgba(0,0,0,0.2)',
                          background: `linear-gradient(135deg, ${getBalanceColor(Number(selectedUser.balance))} 0%, ${alpha(getBalanceColor(Number(selectedUser.balance)), 0.8)} 100%)`,
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent'
                        }}
                      >
                        ₦{selectedUser.balance !== undefined ? Number(selectedUser.balance).toLocaleString() : '0'}
                      </Typography>
                    </Paper>
                  </Grid>

                  {/* Basic Info */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{
                      p: 3,
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.05)'
                        : 'rgba(255,255,255,0.8)',
                      backdropFilter: 'blur(10px)',
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 16px rgba(0,0,0,0.2)'
                        : '0 4px 16px rgba(0,0,0,0.05)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 8px 24px rgba(0,0,0,0.3)'
                          : '0 8px 24px rgba(0,0,0,0.1)'
                      }
                    }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                        👤 Username
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="text.primary">
                        {selectedUser.username || '-'}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper sx={{
                      p: 3,
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.05)'
                        : 'rgba(255,255,255,0.8)',
                      backdropFilter: 'blur(10px)',
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 16px rgba(0,0,0,0.2)'
                        : '0 4px 16px rgba(0,0,0,0.05)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 8px 24px rgba(0,0,0,0.3)'
                          : '0 8px 24px rgba(0,0,0,0.1)'
                      }
                    }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                        📱 Phone
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="text.primary">
                        {selectedUser.phone || '-'}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper sx={{
                      p: 3,
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.05)'
                        : 'rgba(255,255,255,0.8)',
                      backdropFilter: 'blur(10px)',
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 16px rgba(0,0,0,0.2)'
                        : '0 4px 16px rgba(0,0,0,0.05)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 8px 24px rgba(0,0,0,0.3)'
                          : '0 8px 24px rgba(0,0,0,0.1)'
                      }
                    }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                        🚻 Gender
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="text.primary">
                        {selectedUser.gender || '-'}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper sx={{
                      p: 3,
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.05)'
                        : 'rgba(255,255,255,0.8)',
                      backdropFilter: 'blur(10px)',
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 16px rgba(0,0,0,0.2)'
                        : '0 4px 16px rgba(0,0,0,0.05)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 8px 24px rgba(0,0,0,0.3)'
                          : '0 8px 24px rgba(0,0,0,0.1)'
                      }
                    }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                        📅 Joined Date
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="text.primary">
                        {selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        }) : '-'}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Verification Status */}
                <Typography variant="h5" fontWeight={700} sx={{
                  mb: 4,
                  mt: 6,
                  color: theme.palette.text.primary,
                  textAlign: 'center',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 60,
                    height: 3,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 2
                  }
                }}>
                  🔐 Verification Status
                </Typography>

                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{
                      p: 3,
                      borderRadius: 3,
                      textAlign: 'center',
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.05)'
                        : 'rgba(255,255,255,0.8)',
                      backdropFilter: 'blur(10px)',
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 16px rgba(0,0,0,0.2)'
                        : '0 4px 16px rgba(0,0,0,0.05)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 8px 24px rgba(0,0,0,0.3)'
                          : '0 8px 24px rgba(0,0,0,0.1)'
                      }
                    }}>
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                        ✅ Account Verified
                      </Typography>
                      <Chip
                        label={selectedUser.isVerified ? 'Yes' : 'No'}
                        color={selectedUser.isVerified ? 'success' : 'default'}
                        size="large"
                        sx={{
                          fontWeight: 700,
                          fontSize: '1rem',
                          px: 2,
                          py: 1,
                          borderRadius: 3,
                          boxShadow: selectedUser.isVerified ? '0 4px 12px rgba(76, 175, 80, 0.3)' : 'none'
                        }}
                      />
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{
                      p: 3,
                      borderRadius: 3,
                      textAlign: 'center',
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.05)'
                        : 'rgba(255,255,255,0.8)',
                      backdropFilter: 'blur(10px)',
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 16px rgba(0,0,0,0.2)'
                        : '0 4px 16px rgba(0,0,0,0.05)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 8px 24px rgba(0,0,0,0.3)'
                          : '0 8px 24px rgba(0,0,0,0.1)'
                      }
                    }}>
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                        🔐 KYC Verified
                      </Typography>
                      <Chip
                        label={selectedUser.isKycVerified ? 'Yes' : 'No'}
                        color={selectedUser.isKycVerified ? 'success' : 'default'}
                        size="large"
                        sx={{
                          fontWeight: 700,
                          fontSize: '1rem',
                          px: 2,
                          py: 1,
                          borderRadius: 3,
                          boxShadow: selectedUser.isKycVerified ? '0 4px 12px rgba(76, 175, 80, 0.3)' : 'none'
                        }}
                      />
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{
                      p: 3,
                      borderRadius: 3,
                      textAlign: 'center',
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.05)'
                        : 'rgba(255,255,255,0.8)',
                      backdropFilter: 'blur(10px)',
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 16px rgba(0,0,0,0.2)'
                        : '0 4px 16px rgba(0,0,0,0.05)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 8px 24px rgba(0,0,0,0.3)'
                          : '0 8px 24px rgba(0,0,0,0.1)'
                      }
                    }}>
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                        🪪 NIN Match
                      </Typography>
                      <Tooltip title={selectedUser.isKycninVerified ? (selectedUser.ninResponseName || 'Data exists') : 'Not verified'}>
                        <Chip
                          label={selectedUser.isKycninVerified ? (selectedUser.isNinNameMatched ? 'Match' : 'Mismatch') : 'N/A'}
                          color={selectedUser.isKycninVerified ? (selectedUser.isNinNameMatched ? 'success' : 'warning') : 'default'}
                          size="large"
                          sx={{
                            fontWeight: 700,
                            fontSize: '1rem',
                            px: 2,
                            py: 1,
                            borderRadius: 3,
                          }}
                        />
                      </Tooltip>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{
                      p: 3,
                      borderRadius: 3,
                      textAlign: 'center',
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.05)'
                        : 'rgba(255,255,255,0.8)',
                      backdropFilter: 'blur(10px)',
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 16px rgba(0,0,0,0.2)'
                        : '0 4px 16px rgba(0,0,0,0.05)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 8px 24px rgba(0,0,0,0.3)'
                          : '0 8px 24px rgba(0,0,0,0.1)'
                      }
                    }}>
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                        💳 BVN Match
                      </Typography>
                      <Tooltip title={selectedUser.isKycbvnVerified ? (selectedUser.bvnResponseName || 'Data exists') : 'Not verified'}>
                        <Chip
                          label={selectedUser.isKycbvnVerified ? (selectedUser.isBvnNameMatched ? 'Match' : 'Mismatch') : 'N/A'}
                          color={selectedUser.isKycbvnVerified ? (selectedUser.isBvnNameMatched ? 'success' : 'info') : 'default'}
                          size="large"
                          sx={{
                            fontWeight: 700,
                            fontSize: '1rem',
                            px: 2,
                            py: 1,
                            borderRadius: 3,
                          }}
                        />
                      </Tooltip>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{
                      p: 3,
                      borderRadius: 3,
                      textAlign: 'center',
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.05)'
                        : 'rgba(255,255,255,0.8)',
                      backdropFilter: 'blur(10px)',
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 16px rgba(0,0,0,0.2)'
                        : '0 4px 16px rgba(0,0,0,0.05)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 8px 24px rgba(0,0,0,0.3)'
                          : '0 8px 24px rgba(0,0,0,0.1)'
                      }
                    }}>
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                        🆔 NIN Verified
                      </Typography>
                      <Chip
                        label={selectedUser.isKycninVerified ? 'Yes' : 'No'}
                        color={selectedUser.isKycninVerified ? 'success' : 'default'}
                        size="large"
                        sx={{
                          fontWeight: 700,
                          fontSize: '1rem',
                          px: 2,
                          py: 1,
                          borderRadius: 3,
                          boxShadow: selectedUser.isKycninVerified ? '0 4px 12px rgba(76, 175, 80, 0.3)' : 'none'
                        }}
                      />
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{
                      p: 3,
                      borderRadius: 3,
                      textAlign: 'center',
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.05)'
                        : 'rgba(255,255,255,0.8)',
                      backdropFilter: 'blur(10px)',
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 16px rgba(0,0,0,0.2)'
                        : '0 4px 16px rgba(0,0,0,0.05)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 8px 24px rgba(0,0,0,0.3)'
                          : '0 8px 24px rgba(0,0,0,0.1)'
                      }
                    }}>
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                        🏦 BVN Verified
                      </Typography>
                      <Chip
                        label={selectedUser.isKycbvnVerified ? 'Yes' : 'No'}
                        color={selectedUser.isKycbvnVerified ? 'success' : 'default'}
                        size="large"
                        sx={{
                          fontWeight: 700,
                          fontSize: '1rem',
                          px: 2,
                          py: 1,
                          borderRadius: 3,
                          boxShadow: selectedUser.isKycbvnVerified ? '0 4px 12px rgba(76, 175, 80, 0.3)' : 'none'
                        }}
                      />
                    </Paper>
                  </Grid>
                </Grid>

                {/* Identity Information */}
                <Typography variant="h5" fontWeight={700} sx={{
                  mb: 4,
                  color: theme.palette.text.primary,
                  textAlign: 'center',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 60,
                    height: 3,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 2
                  }
                }}>
                  🆔 Identity Information
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{
                      p: 3,
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.05)'
                        : 'rgba(255,255,255,0.8)',
                      backdropFilter: 'blur(10px)',
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 16px rgba(0,0,0,0.2)'
                        : '0 4px 16px rgba(0,0,0,0.05)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 8px 24px rgba(0,0,0,0.3)'
                          : '0 8px 24px rgba(0,0,0,0.1)'
                      }
                    }}>
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                        🆔 NIN Number
                      </Typography>
                      <Typography variant="h6" fontWeight={700} fontFamily="monospace" sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}>
                        {selectedUser.nin || selectedUser.vninNo || '-'}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{
                      p: 3,
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.05)'
                        : 'rgba(255,255,255,0.8)',
                      backdropFilter: 'blur(10px)',
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 16px rgba(0,0,0,0.2)'
                        : '0 4px 16px rgba(0,0,0,0.05)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 8px 24px rgba(0,0,0,0.3)'
                          : '0 8px 24px rgba(0,0,0,0.1)'
                      }
                    }}>
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                        🏦 BVN Number
                      </Typography>
                      <Typography variant="h6" fontWeight={700} fontFamily="monospace" sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}>
                        {maskBvn(selectedUser.bvn || selectedUser.vbvnNo || '')}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Virtual Account Information */}
                {(selectedUser.virtuaAccountBank || selectedUser.virtuaAccountName || selectedUser.virtuaAccountNo) && (
                  <>
                    <Typography variant="h5" fontWeight={700} sx={{
                      mb: 4,
                      mt: 6,
                      color: theme.palette.text.primary,
                      textAlign: 'center',
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: -8,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 60,
                        height: 3,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: 2
                      }
                    }}>
                      🏛️ Virtual Account
                    </Typography>

                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4}>
                        <Paper sx={{
                          p: 3,
                          borderRadius: 3,
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          background: theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.05)'
                            : 'rgba(255,255,255,0.8)',
                          backdropFilter: 'blur(10px)',
                          boxShadow: theme.palette.mode === 'dark'
                            ? '0 4px 16px rgba(0,0,0,0.2)'
                            : '0 4px 16px rgba(0,0,0,0.05)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: theme.palette.mode === 'dark'
                              ? '0 8px 24px rgba(0,0,0,0.3)'
                              : '0 8px 24px rgba(0,0,0,0.1)'
                          }
                        }}>
                          <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                            🏛️ Bank
                          </Typography>
                          <Typography variant="h6" fontWeight={700} color="text.primary">
                            {selectedUser.virtuaAccountBank || '-'}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Paper sx={{
                          p: 3,
                          borderRadius: 3,
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          background: theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.05)'
                            : 'rgba(255,255,255,0.8)',
                          backdropFilter: 'blur(10px)',
                          boxShadow: theme.palette.mode === 'dark'
                            ? '0 4px 16px rgba(0,0,0,0.2)'
                            : '0 4px 16px rgba(0,0,0,0.05)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: theme.palette.mode === 'dark'
                              ? '0 8px 24px rgba(0,0,0,0.3)'
                              : '0 8px 24px rgba(0,0,0,0.1)'
                          }
                        }}>
                          <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                            👤 Account Name
                          </Typography>
                          <Typography variant="h6" fontWeight={700} color="text.primary">
                            {selectedUser.virtuaAccountName || '-'}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Paper sx={{
                          p: 3,
                          borderRadius: 3,
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          background: theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.05)'
                            : 'rgba(255,255,255,0.8)',
                          backdropFilter: 'blur(10px)',
                          boxShadow: theme.palette.mode === 'dark'
                            ? '0 4px 16px rgba(0,0,0,0.2)'
                            : '0 4px 16px rgba(0,0,0,0.05)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: theme.palette.mode === 'dark'
                              ? '0 8px 24px rgba(0,0,0,0.3)'
                              : '0 8px 24px rgba(0,0,0,0.1)'
                          }
                        }}>
                          <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                            🔢 Account Number
                          </Typography>
                          <Typography variant="h6" fontWeight={700} fontFamily="monospace" sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                          }}>
                            {selectedUser.virtuaAccountNo || '-'}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </>
                )}
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Fund Wallet Dialog */}
      <Dialog
        open={fundWalletOpen}
        onClose={() => {
          setFundWalletOpen(false);
          setAmount('');

        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary?.main || theme.palette.primary.dark})`,
            color: 'white',
            py: 3,
            px: 4,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 1,
              background: 'rgba(255, 255, 255, 0.1)',
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
              }}
            >
              <AccountBalanceWalletIcon sx={{ fontSize: 28, color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                Fund Wallet
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Credit or debit user wallet
              </Typography>
            </Box>
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => {
              setFundWalletOpen(false);
              setAmount('');

            }}
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: 4,
            bgcolor: theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.default, 0.5)
              : alpha(theme.palette.background.default, 0.3),
          }}
        >
          {selectedUser && (
            <>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mb: 3,
                  borderRadius: 3,
                  bgcolor: theme.palette.background.paper,
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  textAlign: 'center',
                }}
              >
                <Avatar
                  src={selectedUser.profilePic || ''}
                  alt={selectedUser.name || selectedUser.fullname || 'User'}
                  sx={{
                    width: 80,
                    height: 80,
                    mb: 2,
                    mx: 'auto',
                    border: `3px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                  }}
                >
                  {!(selectedUser.profilePic) && (selectedUser.name?.[0] || selectedUser.fullname?.[0] || '?')}
                </Avatar>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                  {selectedUser.name || selectedUser.fullname || '-'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {selectedUser.email}
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Current Balance
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color="primary.main">
                    ₦{Number(selectedUser.balance || 0).toLocaleString()}
                  </Typography>
                </Box>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mb: 3,
                  borderRadius: 3,
                  bgcolor: theme.palette.background.paper,
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2.5, color: 'text.primary' }}>
                  Transaction Details
                </Typography>

                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, color: 'text.primary' }}>
                    Transaction Type <Typography component="span" sx={{ color: 'error.main' }}>*</Typography>
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={fundType}
                      onChange={(e) => setFundType(e.target.value)}
                      sx={{
                        borderRadius: 2.5,
                        bgcolor: theme.palette.mode === 'dark'
                          ? alpha(theme.palette.background.default, 0.5)
                          : alpha(theme.palette.background.default, 0.8),
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          bgcolor: theme.palette.background.paper,
                        },
                        '&.Mui-focused': {
                          bgcolor: theme.palette.background.paper,
                          boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
                        },
                      }}
                    >
                      <MenuItem value="credit">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '6px',
                              bgcolor: alpha(theme.palette.success.main, 0.1),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <AddIcon sx={{ color: theme.palette.success.main, fontSize: 16 }} />
                          </Box>
                          <Typography>Credit</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="debit">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '6px',
                              bgcolor: alpha(theme.palette.error.main, 0.1),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <RemoveIcon sx={{ color: theme.palette.error.main, fontSize: 16 }} />
                          </Box>
                          <Typography>Debit</Typography>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, color: 'text.primary' }}>
                    Amount <Typography component="span" sx={{ color: 'error.main' }}>*</Typography>
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={amount}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only allow positive numbers
                      if (value === '' || (Number(value) >= 0)) {
                        setAmount(value);
                      }
                    }}
                    placeholder="Enter amount"
                    inputProps={{
                      min: 0,
                      step: 1,
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2.5,
                        bgcolor: theme.palette.mode === 'dark'
                          ? alpha(theme.palette.background.default, 0.5)
                          : alpha(theme.palette.background.default, 0.8),
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          bgcolor: theme.palette.background.paper,
                        },
                        '&.Mui-focused': {
                          bgcolor: theme.palette.background.paper,
                          boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
                        },
                      },
                    }}
                  />
                  {amount && (
                    <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.1), border: `1px solid ${alpha(theme.palette.info.main, 0.2)}` }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        New Balance
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color={fundType === 'credit' ? 'success.main' : 'error.main'}>
                        ₦{(
                          fundType === 'credit'
                            ? Number(selectedUser.balance || 0) + Number(amount)
                            : Math.max(0, Number(selectedUser.balance || 0) - Number(amount))
                        ).toLocaleString()}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>

              {fundType === 'debit' && Number(amount) > Number(selectedUser.balance) && (
                <Alert
                  severity="error"
                  icon={<WarningIcon />}
                  sx={{
                    mb: 2,
                    borderRadius: 2.5,
                    '& .MuiAlert-icon': {
                      fontSize: 24,
                    },
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    Insufficient Balance
                  </Typography>
                  <Typography variant="caption">
                    The user's current balance is insufficient for this debit operation.
                  </Typography>
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            bgcolor: theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.default, 0.5)
              : alpha(theme.palette.background.default, 0.3),
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Button
            onClick={() => {
              setFundWalletOpen(false);
              setAmount('');

            }}
            variant="outlined"
            sx={{
              borderRadius: 2.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1.5,
              borderColor: alpha(theme.palette.divider, 0.3),
              '&:hover': {
                borderColor: theme.palette.error.main,
                bgcolor: alpha(theme.palette.error.main, 0.05),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleFundWalletClick}
            variant="contained"
            color="primary"
            disabled={loading || !amount || (fundType === 'debit' && Number(amount) > Number(selectedUser?.balance))}
            startIcon={!loading && <AccountBalanceWalletIcon />}
            sx={{
              borderRadius: 2.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              py: 1.5,
              minWidth: 180,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary?.main || theme.palette.primary.dark})`,
              boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary?.dark || theme.palette.primary.main})`,
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
                transform: 'translateY(-2px)',
              },
              '&:disabled': {
                background: theme.palette.action.disabledBackground,
                boxShadow: 'none',
              },
              transition: 'all 0.3s ease',
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'white' }} />
                Processing...
              </Box>
            ) : (
              'Fund Wallet'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* OTP Dialog */}
      <AdminOTPDialog
        open={otpDialogOpen}
        onClose={() => {
          setOtpDialogOpen(false);
          setPendingTransaction(null);
          if (otpOperationType === 'bulk_operation') {
            setIsBulkFunding(false);
          }
        }}
        operationType={otpOperationType}
        onVerify={handleOTPVerify}
      />

      {/* Bulk Fund Wallet Dialog */}
      <Dialog
        open={bulkFundOpen}
        onClose={handleBulkFundClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary?.main || theme.palette.primary.dark})`,
            color: 'white',
            py: 3,
            px: 4,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 1,
              background: 'rgba(255, 255, 255, 0.1)',
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
              }}
            >
              <AccountBalanceWalletIcon sx={{ fontSize: 28, color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                Bulk Fund Wallets
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Fund multiple wallets at once
              </Typography>
            </Box>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleBulkFundClose}
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: 4,
            bgcolor: theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.default, 0.5)
              : alpha(theme.palette.background.default, 0.3),
          }}
        >
          <Box
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 3,
              bgcolor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.info.main, 0.1)
                : alpha(theme.palette.info.main, 0.05),
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
            }}
          >
            <InfoIcon sx={{ color: theme.palette.info.main, mt: 0.5, flexShrink: 0 }} />
            <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.6 }}>
              Select users and enter amounts to fund their wallets. Perfect for salary payments and bulk transfers.
            </Typography>
          </Box>

          {/* Transaction Type, Purpose, and Narration */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              bgcolor: theme.palette.background.paper,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2.5, color: 'text.primary' }}>
              Transaction Details
            </Typography>
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, color: 'text.primary' }}>
                  Transaction Type <Typography component="span" sx={{ color: 'error.main' }}>*</Typography>
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={bulkFundType}
                    onChange={(e) => setBulkFundType(e.target.value)}
                    sx={{
                      borderRadius: 2.5,
                      bgcolor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.background.default, 0.5)
                        : alpha(theme.palette.background.default, 0.8),
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: theme.palette.background.paper,
                      },
                      '&.Mui-focused': {
                        bgcolor: theme.palette.background.paper,
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
                      },
                    }}
                  >
                    <MenuItem value="credit">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '6px',
                            bgcolor: alpha(theme.palette.success.main, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <AddIcon sx={{ color: theme.palette.success.main, fontSize: 16 }} />
                        </Box>
                        <Typography>Credit</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="debit">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '6px',
                            bgcolor: alpha(theme.palette.error.main, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <RemoveIcon sx={{ color: theme.palette.error.main, fontSize: 16 }} />
                        </Box>
                        <Typography>Debit</Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, color: 'text.primary' }}>
                  Purpose <Typography component="span" sx={{ color: 'error.main' }}>*</Typography>
                </Typography>
                <FormControl fullWidth required>
                  <Select
                    value={bulkPurpose}
                    onChange={(e) => setBulkPurpose(e.target.value)}
                    required
                    sx={{
                      borderRadius: 2.5,
                      bgcolor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.background.default, 0.5)
                        : alpha(theme.palette.background.default, 0.8),
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: theme.palette.background.paper,
                      },
                      '&.Mui-focused': {
                        bgcolor: theme.palette.background.paper,
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
                      },
                    }}
                  >
                    <MenuItem value="Salary Payment">Salary Payment</MenuItem>
                    <MenuItem value="Bonus">Bonus</MenuItem>
                    <MenuItem value="Loan">Loan</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, color: 'text.primary' }}>
                  Narration
                </Typography>
                <TextField
                  fullWidth
                  value={bulkNarration}
                  onChange={(e) => setBulkNarration(e.target.value)}
                  placeholder="Enter transaction narration"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2.5,
                      bgcolor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.background.default, 0.5)
                        : alpha(theme.palette.background.default, 0.8),
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: theme.palette.background.paper,
                      },
                      '&.Mui-focused': {
                        bgcolor: theme.palette.background.paper,
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
                      },
                    },
                  }}
                  helperText="This will be recorded in transaction history"
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Group Management Buttons */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 3,
              bgcolor: theme.palette.background.paper,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, color: 'text.primary' }}>
              Group Management
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={handleSaveGroupOpen}
                disabled={selectedUsers.length === 0}
                sx={{
                  borderRadius: 2.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                  },
                }}
              >
                Save as Group
              </Button>
              <Button
                variant="outlined"
                startIcon={<FolderIcon />}
                onClick={handleLoadGroupsOpen}
                sx={{
                  borderRadius: 2.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                  },
                }}
              >
                Load Group
              </Button>
            </Box>
          </Paper>

          {/* Excel Import/Export Section */}
          <Paper
            elevation={0}
            sx={{
              mb: 3,
              p: 3,
              borderRadius: 3,
              bgcolor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.success.main, 0.05)
                : alpha(theme.palette.success.main, 0.03),
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <DownloadIcon sx={{ color: theme.palette.success.main, fontSize: 22 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Excel Import/Export
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
              Import users by Phone Number or Virtual Account Number from Excel file. The system will automatically fetch user details (username, fullname) using the identifiers provided.
            </Typography>
            <Box
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                bgcolor: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.background.default, 0.5)
                  : alpha(theme.palette.background.default, 0.8),
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.6 }}>
                <strong>Required columns:</strong> "Phone Number" or "Virtual Account Number" (or "Account Number", "VA") and "Amount".
                <br />
                <strong>Optional:</strong> "Narration" (or "Description", "Note").
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={downloadTemplate}
                sx={{
                  borderRadius: 2.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  bgcolor: theme.palette.success.main,
                  '&:hover': {
                    bgcolor: theme.palette.success.dark,
                  },
                }}
              >
                Download Template
              </Button>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                component="label"
                disabled={isImporting}
                sx={{
                  borderRadius: 2.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                  },
                }}
              >
                {isImporting ? 'Importing...' : 'Import Excel'}
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                />
              </Button>
              {excelFile && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                  }}
                >
                  <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 18 }} />
                  <Typography variant="body2" color="success.main" fontWeight={500}>
                    {excelFile.name}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>

          {/* Quick Amount Buttons */}
          {selectedUsers.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                mb: 3,
                p: 2.5,
                borderRadius: 3,
                bgcolor: theme.palette.background.paper,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, color: 'text.primary' }}>
                Quick Amount Set
                <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary', fontWeight: 400 }}>
                  (for {selectedUsers.length} selected user{selectedUsers.length > 1 ? 's' : ''})
                </Typography>
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {[1000, 2000, 5000, 10000, 20000, 50000].map((amount) => (
                  <Button
                    key={amount}
                    variant="outlined"
                    size="small"
                    onClick={() => setAmountForSelected(amount)}
                    sx={{
                      borderRadius: 2.5,
                      textTransform: 'none',
                      minWidth: 'auto',
                      px: 2.5,
                      py: 1,
                      fontWeight: 600,
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      color: theme.palette.primary.main,
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    ₦{amount.toLocaleString()}
                  </Button>
                ))}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setAmountForSelected('')}
                  color="error"
                  sx={{
                    borderRadius: 2.5,
                    textTransform: 'none',
                    minWidth: 'auto',
                    px: 2.5,
                    py: 1,
                    fontWeight: 600,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 8px ${alpha(theme.palette.error.main, 0.2)}`,
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Clear
                </Button>
              </Box>
            </Paper>
          )}

          {/* Saved Groups Display */}
          {beneficiaryGroups.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Saved Groups
              </Typography>
              <Grid container spacing={2}>
                {beneficiaryGroups.slice(0, 3).map((group) => (
                  <Grid item xs={12} md={4} key={group._id}>
                    <Card sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      }
                    }}>
                      <CardContent sx={{ pb: 1 }}>
                        <Typography variant="h6" fontWeight={600} noWrap>
                          {group.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {group.description}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {group.beneficiaries.length} beneficiaries • ₦{group.totalAmount.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Last used: {new Date(group.lastUsed).toLocaleDateString()}
                        </Typography>
                      </CardContent>
                      <CardActions sx={{ pt: 0, px: 2, pb: 2 }}>
                        <Button
                          size="small"
                          startIcon={<PlayArrowIcon />}
                          onClick={() => handleUseGroupOpen(group)}
                          sx={{ textTransform: 'none' }}
                        >
                          Use
                        </Button>
                        <Button
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => handleLoadGroup(group)}
                          sx={{ textTransform: 'none' }}
                        >
                          Load
                        </Button>
                        <Button
                          size="small"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleDeleteGroup(group._id, group.name)}
                          color="error"
                          sx={{ textTransform: 'none' }}
                        >
                          Delete
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* User Selection and Amounts */}
          <Paper
            elevation={0}
            sx={{
              mb: 3,
              p: 3,
              borderRadius: 3,
              bgcolor: theme.palette.background.paper,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 600, color: 'text.primary' }}>
              Select Users and Enter Amounts
            </Typography>

            {/* Search field for users */}
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search users by name, email, username or phone..."
              value={bulkUserFilter}
              onChange={handleBulkUserFilterChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2.5,
                  bgcolor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.default, 0.5)
                    : alpha(theme.palette.background.default, 0.8),
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: theme.palette.background.paper,
                  },
                  '&.Mui-focused': {
                    bgcolor: theme.palette.background.paper,
                    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
                  },
                },
              }}
            />

            {/* User count and select all */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {bulkFilteredUsers.length} user{bulkFilteredUsers.length !== 1 ? 's' : ''}
                {bulkUserFilter && ` (filtered from ${users.length} total)`}
              </Typography>
              {bulkFilteredUsers.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Checkbox
                    checked={selectedUsers.length === bulkFilteredUsers.length && bulkFilteredUsers.length > 0}
                    indeterminate={selectedUsers.length > 0 && selectedUsers.length < bulkFilteredUsers.length}
                    onChange={handleSelectAllBulk}
                    size="small"
                  />
                  <Typography variant="body2">
                    Select All ({selectedUsers.length} selected)
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {paginatedBulkUsers.map((user) => {
                const isSelected = userSelectionStatus[user._id];
                const userAmount = userAmounts[user._id] || '';

                return (
                  <Paper
                    key={user._id}
                    sx={{
                      p: 2,
                      mb: 2,
                      border: isSelected ? `2px solid ${theme.palette.primary.main}` : '1px solid',
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      borderRadius: 2,
                      backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: alpha(theme.palette.primary.main, 0.02),
                      },
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={1}>
                        <Checkbox
                          checked={isSelected}
                          onChange={(e) => handleUserSelection(user, e.target.checked)}
                          color="primary"
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar
                            src={user.profilePic || ''}
                            alt={user.name || user.fullname || 'User'}
                            sx={{ width: 32, height: 32 }}
                          >
                            {!(user.profilePic) && (user.name?.[0] || user.fullname?.[0] || '?')}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {user.name || user.fullname || '-'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.email}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Current Balance: ₦{Number(user.balance || 0).toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Amount"
                          type="number"
                          value={userAmount}
                          onChange={(e) => handleAmountChange(user._id, e.target.value)}
                          disabled={!isSelected}
                          inputProps={{
                            min: 0,
                            step: 1,
                          }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                          }}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        {isSelected && userAmount && (
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              New Balance: ₦{(
                                bulkFundType === 'credit'
                                  ? Number(user.balance || 0) + Number(userAmount) - (Number(userAmount) > 10000 ? 50 : 0)
                                  : Math.max(0, Number(user.balance || 0) - Number(userAmount))
                              ).toLocaleString()}
                            </Typography>
                            {bulkFundType === 'credit' && Number(userAmount) > 10000 && (
                              <Typography variant="caption" color="warning.main">
                                (₦50 levy deducted)
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                  </Paper>
                );
              })}
            </Box>

            {/* Pagination for bulk users in save dialog */}
            {bulkUserTotalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  count={bulkUserTotalPages}
                  page={bulkUserPage}
                  onChange={handleBulkUserPageChange}
                  color="primary"
                  size="small"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </Paper>

          {/* Summary */}
          {selectedUsers.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mt: 3,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary?.main || theme.palette.primary.light, 0.05)})`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '10px',
                    bgcolor: alpha(theme.palette.primary.main, 0.15),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AccountBalanceWalletIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  Transaction Summary
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: theme.palette.background.paper,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Selected Users
                    </Typography>
                    <Typography variant="h6" fontWeight={700} color="primary.main">
                      {selectedUsers.length}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: theme.palette.background.paper,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Total Amount
                    </Typography>
                    <Typography variant="h6" fontWeight={700} color="success.main">
                      ₦{selectedUsers.reduce((sum, user) =>
                        sum + Number(userAmounts[user._id] || 0), 0
                      ).toLocaleString()}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: theme.palette.background.paper,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Transaction Type
                    </Typography>
                    <Chip
                      label={bulkFundType === 'credit' ? 'Credit' : 'Debit'}
                      color={bulkFundType === 'credit' ? 'success' : 'error'}
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                </Grid>
                {bulkNarration && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: theme.palette.background.paper,
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Narration
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {bulkNarration}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {bulkFundType === 'credit' && selectedUsers.some(user => Number(userAmounts[user._id] || 0) > 10000) && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                      }}
                    >
                      <WarningIcon sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
                      <Typography variant="body2" color="warning.main" fontWeight={500}>
                        Electronic Levy (₦50) will be deducted from amounts over ₦10,000
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            bgcolor: theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.default, 0.5)
              : alpha(theme.palette.background.default, 0.3),
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Button
            onClick={handleBulkFundClose}
            variant="outlined"
            sx={{
              borderRadius: 2.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1.5,
              borderColor: alpha(theme.palette.divider, 0.3),
              '&:hover': {
                borderColor: theme.palette.error.main,
                bgcolor: alpha(theme.palette.error.main, 0.05),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkFundSubmit}
            variant="contained"
            color="primary"
            disabled={isBulkFunding || selectedUsers.length === 0 || !bulkNarration.trim()}
            startIcon={!isBulkFunding && <AccountBalanceWalletIcon />}
            sx={{
              borderRadius: 2.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              py: 1.5,
              minWidth: 200,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary?.main || theme.palette.primary.dark})`,
              boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary?.dark || theme.palette.primary.main})`,
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
                transform: 'translateY(-2px)',
              },
              '&:disabled': {
                background: theme.palette.action.disabledBackground,
                boxShadow: 'none',
              },
              transition: 'all 0.3s ease',
            }}
          >
            {isBulkFunding ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'white' }} />
                Processing...
              </Box>
            ) : (
              `Fund ${selectedUsers.length} User${selectedUsers.length > 1 ? 's' : ''}`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Group Dialog */}
      <Dialog
        open={saveGroupOpen}
        onClose={handleSaveGroupClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Save Group
          <IconButton
            aria-label="close"
            onClick={handleSaveGroupClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Enter group name and description, then select users to include in the group.
          </Typography>

          <TextField
            fullWidth
            label="Group Name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Group Description"
            value={groupDescription}
            onChange={(e) => setGroupDescription(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Select Users to Include
          </Typography>

          {/* Search field for users */}
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Search users by name, email, username or phone..."
            value={bulkUserFilter}
            onChange={handleBulkUserFilterChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {/* User count and select all */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {bulkFilteredUsers.length} user{bulkFilteredUsers.length !== 1 ? 's' : ''}
              {bulkUserFilter && ` (filtered from ${users.length} total)`}
            </Typography>
            {bulkFilteredUsers.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Checkbox
                  checked={selectedUsers.length === bulkFilteredUsers.length && bulkFilteredUsers.length > 0}
                  indeterminate={selectedUsers.length > 0 && selectedUsers.length < bulkFilteredUsers.length}
                  onChange={handleSelectAllBulk}
                  size="small"
                />
                <Typography variant="body2">
                  Select All ({selectedUsers.length} selected)
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {paginatedBulkUsers.map((user) => {
              const isSelected = userSelectionStatus[user._id];
              const userAmount = userAmounts[user._id] || '';

              return (
                <Paper
                  key={user._id}
                  sx={{
                    p: 2,
                    mb: 2,
                    border: isSelected ? `2px solid ${theme.palette.primary.main}` : '1px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: alpha(theme.palette.primary.main, 0.02),
                    },
                  }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={1}>
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => handleUserSelection(user, e.target.checked)}
                        color="primary"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar
                          src={user.profilePic || ''}
                          alt={user.name || user.fullname || 'User'}
                          sx={{ width: 32, height: 32 }}
                        >
                          {!(user.profilePic) && (user.name?.[0] || user.fullname?.[0] || '?')}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {user.name || user.fullname || '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        Current Balance: ₦{Number(user.balance || 0).toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Amount"
                        type="number"
                        value={userAmount}
                        onChange={(e) => handleAmountChange(user._id, e.target.value)}
                        disabled={!isSelected}
                        inputProps={{
                          min: 0,
                          step: 1,
                        }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                        }}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      {isSelected && userAmount && (
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            New Balance: ₦{(
                              bulkFundType === 'credit'
                                ? Number(user.balance || 0) + Number(userAmount) - (Number(userAmount) > 10000 ? 50 : 0)
                                : Math.max(0, Number(user.balance || 0) - Number(userAmount))
                            ).toLocaleString()}
                          </Typography>
                          {bulkFundType === 'credit' && Number(userAmount) > 10000 && (
                            <Typography variant="caption" color="warning.main">
                              (₦50 levy deducted)
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              );
            })}
          </Box>

          {/* Pagination for bulk users in save dialog */}
          {bulkUserTotalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={bulkUserTotalPages}
                page={bulkUserPage}
                onChange={handleBulkUserPageChange}
                color="primary"
                size="small"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleSaveGroupClose} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSaveGroup}
            variant="contained"
            color="primary"
            disabled={isSavingGroup || selectedUsers.length === 0 || !groupName.trim() || !groupDescription.trim()}
            startIcon={isSavingGroup ? <CircularProgress size={16} /> : null}
          >
            {isSavingGroup ? 'Saving...' : 'Save Group'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Load Groups Dialog */}
      <Dialog
        open={loadGroupsOpen}
        onClose={handleLoadGroupsClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Load Group
          <IconButton
            aria-label="close"
            onClick={handleLoadGroupsClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Select a group to load its beneficiaries.
          </Typography>

          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Select Group
          </Typography>

          {/* Group selection */}
          <Select
            fullWidth
            value={selectedGroup ? selectedGroup._id : ''}
            onChange={(e) => {
              if (e.target.value) {
                handleLoadGroup(beneficiaryGroups.find(g => g._id === e.target.value));
              }
            }}
          >
            <MenuItem value="">Select a group</MenuItem>
            {beneficiaryGroups.map((group) => (
              <MenuItem key={group._id} value={group._id}>
                {group.name}
              </MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleLoadGroupsClose} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleLoadGroup}
            variant="contained"
            color="primary"
            disabled={!selectedGroup}
          >
            Load Group
          </Button>
        </DialogActions>
      </Dialog>

      {/* Use Group Dialog */}
      <Dialog
        open={useGroupOpen}
        onClose={handleUseGroupClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Use Group for Bulk Funding
          <IconButton
            aria-label="close"
            onClick={handleUseGroupClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedGroup && (
            <>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {selectedGroup.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {selectedGroup.description}
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Transaction Type</InputLabel>
                    <Select
                      value={groupFundType}
                      label="Transaction Type"
                      onChange={(e) => setGroupFundType(e.target.value)}
                    >
                      <MenuItem value="credit">Credit</MenuItem>
                      <MenuItem value="debit">Debit</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Narration"
                    value={groupNarration}
                    onChange={(e) => setGroupNarration(e.target.value)}
                    placeholder="Enter transaction narration"
                  />
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Beneficiaries ({selectedGroup.beneficiaries.length})
              </Typography>

              <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                {selectedGroup.beneficiaries.map((beneficiary, index) => (
                  <Paper
                    key={beneficiary.userId}
                    sx={{
                      p: 2,
                      mb: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" fontWeight={600}>
                          {beneficiary.userName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {beneficiary.userEmail}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Amount"
                          type="number"
                          value={groupBeneficiaryAmounts[beneficiary.userId] || ''}
                          onChange={e => handleGroupBeneficiaryAmountChange(beneficiary.userId, e.target.value)}
                          inputProps={{ min: 0, step: 1 }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                          }}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="caption" color="text.secondary">
                          {beneficiary.narration || 'No narration'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Box>

              <Paper sx={{ p: 2, mt: 2, backgroundColor: alpha(theme.palette.info.main, 0.1) }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Summary
                </Typography>
                <Typography variant="body2">
                  Total Beneficiaries: {selectedGroup.beneficiaries.length}
                </Typography>
                <Typography variant="body2">
                  Total Amount: ₦{
                    selectedGroup.beneficiaries.reduce((sum, b) =>
                      sum + Number(groupBeneficiaryAmounts[b.userId] || 0), 0
                    ).toLocaleString()
                  }
                </Typography>
                <Typography variant="body2">
                  Transaction Type: {groupFundType === 'credit' ? 'Credit' : 'Debit'}
                </Typography>
                {groupNarration && (
                  <Typography variant="body2">
                    Narration: {groupNarration}
                  </Typography>
                )}
              </Paper>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleUseGroupClose} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleUseGroup}
            variant="contained"
            color="primary"
            disabled={isUsingGroup || !selectedGroup}
            startIcon={isUsingGroup ? <CircularProgress size={16} /> : null}
          >
            {isUsingGroup ? 'Processing...' : `Fund ${selectedGroup?.beneficiaries?.length || 0} Users`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Preview Dialog */}
      <Dialog
        open={showImportPreview}
        onClose={handleImportCancel}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Import Preview
          <IconButton
            aria-label="close"
            onClick={handleImportCancel}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Preview of imported users from Excel file. User details were automatically fetched using the identifiers provided (Phone or VA). Review the data before confirming.
          </Typography>

          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Identifier</TableCell>
                    <TableCell>Username</TableCell>
                    <TableCell>Full Name</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Narration</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {importedUsers.map((user, index) => (
                    <TableRow key={index}>
                      <TableCell>{user.identifier}</TableCell>
                      <TableCell>{user.username}</TableCell>
                      <TableCell>{user.fullname}</TableCell>
                      <TableCell>₦{user.amount.toLocaleString()}</TableCell>
                      <TableCell>{user.narration}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Paper sx={{ p: 2, mt: 2, backgroundColor: alpha(theme.palette.info.main, 0.1) }}>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              Summary
            </Typography>
            <Typography variant="body2">
              Total Users: {importedUsers.length}
            </Typography>
            <Typography variant="body2">
              Total Amount: ₦{importedUsers.reduce((sum, user) => sum + user.amount, 0).toLocaleString()}
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleImportCancel} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={() => {
              // Save as group first, then confirm import
              const groupData = {
                name: `Excel Import - ${new Date().toLocaleDateString()}`,
                description: `Imported from Excel file on ${new Date().toLocaleString()}`,
                beneficiaries: importedUsers.map(user => ({
                  userId: user.userId,
                  userName: user.username,
                  userEmail: user.email,
                  amount: user.amount,
                  narration: user.narration
                }))
              };

              // Save the group
              api.dashboard.createBeneficiaryGroup(groupData)
                .then(() => {
                  toast.success('Group saved successfully!');
                  handleImportConfirm();
                })
                .catch((error) => {
                  console.error('Error saving group:', error);
                  toast.error('Failed to save group, but users will still be imported');
                  handleImportConfirm();
                });
            }}
            variant="outlined"
            color="secondary"
            startIcon={<SaveIcon />}
          >
            Save as Group & Import
          </Button>
          <Button
            onClick={handleImportConfirm}
            variant="contained"
            color="primary"
          >
            Import Only
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AllUsers; 